/**
 * Template color system with variations
 * Each color has: primary, text (darker), secondary (lighter), hover (medium)
 */
export const templateColorPalette = {
  '#2DD0D6': { // Almeria - Cyan/Blue
    primary: '#2DD0D6',
    text: '#115355',
    secondary: '#D8EFEF',
    hover: '#C5E7E7',
    name: 'almeria',
  },
  '#10B981': { // IBERVEG - Green
    primary: '#10B981',
    text: '#085E41',
    secondary: '#D1FAE5',
    hover: '#BAF7D8',
    name: 'iberveg',
  },
  '#F59E0B': { // ACTITURE - Amber/Orange
    primary: '#F59E0B',
    text: '#7A4F05',
    secondary: '#FDEBCE',
    hover: '#FCE1B5',
    name: 'actiture',
  },
  '#8B5CF6': { // ZANOBEET - Purple
    primary: '#8B5CF6',
    text: '#2A0779',
    secondary: '#EDE9FE',
    hover: '#D7CEFD',
    name: 'zanobeet',
  },
  '#126FEF': { // CEBOLLAS - Blue
    primary: '#126FEF',
    text: '#083777',
    secondary: '#CFE2FC',
    hover: '#B7D3FA',
    name: 'cebollas',
  },
  '#EF4444': { // Red
    primary: '#EF4444',
    text: '#7F1D1D',
    secondary: '#FEE2E2',
    hover: '#FECACA',
    name: 'red',
  },
  '#EC4899': { // Pink
    primary: '#EC4899',
    text: '#831843',
    secondary: '#FCE7F3',
    hover: '#FBCFE8',
    name: 'pink',
  },
  '#6B7280': { // Gray
    primary: '#6B7280',
    text: '#374151',
    secondary: '#F3F4F6',
    hover: '#E5E7EB',
    name: 'gray',
  },
  '#14B8A6': { // Teal
    primary: '#14B8A6',
    text: '#134E4A',
    secondary: '#CCFBF1',
    hover: '#99F6E4',
    name: 'teal',
  },
  '#F97316': { // Orange
    primary: '#F97316',
    text: '#7C2D12',
    secondary: '#FFEDD5',
    hover: '#FED7AA',
    name: 'orange',
  },
  '#84CC16': { // Lime
    primary: '#84CC16',
    text: '#365314',
    secondary: '#ECFCCB',
    hover: '#D9F99D',
    name: 'lime',
  },
  '#6366F1': { // Indigo
    primary: '#6366F1',
    text: '#312E81',
    secondary: '#E0E7FF',
    hover: '#C7D2FE',
    name: 'indigo',
  },
};

/**
 * Generate CSS for a template color
 * @param {string} colorHex - The primary color hex code
 * @param {string} templateName - The template name (e.g., 'almeria')
 * @returns {string} CSS string
 */
export const generateTemplateCSS = (colorHex, templateName) => {
  const colors = templateColorPalette[colorHex];
  if (!colors) return '';

  const css = `
/* ${templateName.toUpperCase()} - ${colors.name} */
body.template-${templateName} .bg-primary {
  background-color: ${colors.primary} !important;
}

body.template-${templateName} .text-template-primary {
  color: ${colors.text} !important;
}

body.template-${templateName} button.bg-primary * {
  color: #fff !important;
}

body.template-${templateName} .bg-secondary, body.template-${templateName} .sidebar {
  background-color: ${colors.secondary} !important;
}

body.template-${templateName} .sidebar .session-item.active, body.template-${templateName} .session-item.active, body.template-${templateName} .session-item:hover, body.template-${templateName} .sidebar .collapse-container:hover {
  background-color: ${colors.hover} !important;
}

@keyframes highlight-non-missing-${templateName} {
  0% { background-color: ${colors.secondary}; }
  50% { background-color: ${colors.hover}; }
  100% { background-color: transparent; }
}

body.template-${templateName} .highlight-non-missing {
  animation: highlight-non-missing-${templateName} 5s ease-in-out;
}
`;

  return css;
};

/**
 * Generate CSS for all template colors
 * @returns {string} Complete CSS string
 */
export const generateAllTemplateCSS = () => {
  let css = '/* Template-specific color overrides */\n';
  
  Object.entries(templateColorPalette).forEach(([colorHex, colors]) => {
    css += generateTemplateCSS(colorHex, colors.name);
    css += '\n';
  });
  
  return css;
};

/**
 * Get template name from color hex
 * @param {string} colorHex - The primary color hex code
 * @returns {string} Template name or null
 */
export const getTemplateNameFromColor = (colorHex) => {
  const colors = templateColorPalette[colorHex];
  return colors ? colors.name : null;
};

/**
 * Get CSS class name from template color
 * @param {string} colorHex - The primary color hex code
 * @returns {string} CSS class name (e.g., 'template-almeria') or null
 */
export const getTemplateCSSClassFromColor = (colorHex) => {
  const templateName = getTemplateNameFromColor(colorHex);
  return templateName ? `template-${templateName}` : null;
};

